import { useCallback } from 'react';
import { debounce } from 'lodash';

const DEBOUNCE_WAIT = 1000; // 1 second, matching canvas persistence

interface PersistedLabsState<T> {
  data: T;
  timestamp: number;
  version: string;
}

const CURRENT_VERSION = '1.0.0';

/**
 * Validates the structure of loaded Labs state
 */
const isValidPersistedLabsState = <T>(state: any): state is PersistedLabsState<T> => {
  return (
    state &&
    state.data !== undefined &&
    typeof state.timestamp === 'number' &&
    typeof state.version === 'string'
  );
};

/**
 * Handles version migrations if needed
 */
const migrateLabsState = <T>(state: PersistedLabsState<T>): PersistedLabsState<T> => {
  // For now just return the state as is
  // In the future, implement version-specific migrations here
  return state;
};

/**
 * Active editor tracking for single-state persistence
 */
const ACTIVE_EDITOR_KEY = 'labs-active-editor-id';

/**
 * Get the currently active editor ID
 */
export function getActiveEditorId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_EDITOR_KEY);
  } catch (error) {
    console.error('[Labs Persistence] Failed to get active editor ID:', error);
    return null;
  }
}

/**
 * Set the currently active editor ID
 */
export function setActiveEditorId(editorId: string): void {
  try {
    localStorage.setItem(ACTIVE_EDITOR_KEY, editorId);
  } catch (error) {
    console.error('[Labs Persistence] Failed to set active editor ID:', error);
  }
}

/**
 * Clear all Labs editor storage entries
 */
export function clearAllLabsEditorStorage(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('labs-playbook-editor-') ||
        key.startsWith('labs-schema-editor-') ||
        key.startsWith('labs-integration-editor-') ||
        key.startsWith('labs-contract-editor-')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`[Labs Persistence] Cleared ${keysToRemove.length} editor storage entries`);
  } catch (error) {
    console.error('[Labs Persistence] Failed to clear all editor storage:', error);
  }
}

/**
 * Clear all Labs editor storage entries except for the specified editor ID
 */
export function clearOtherLabsEditorStorage(currentEditorId: string, editorType: string): void {
  try {
    const currentStorageKey = `labs-${editorType}-editor-${currentEditorId}`;
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('labs-playbook-editor-') ||
        key.startsWith('labs-schema-editor-') ||
        key.startsWith('labs-integration-editor-') ||
        key.startsWith('labs-contract-editor-')
      ) && key !== currentStorageKey) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`[Labs Persistence] Cleared ${keysToRemove.length} other editor storage entries, kept ${currentStorageKey}`);
  } catch (error) {
    console.error('[Labs Persistence] Failed to clear other editor storage:', error);
  }
}

/**
 * Clear all Labs UI state storage entries except for the specified editor ID
 */
export function clearOtherLabsUIStorage(currentEditorId: string): void {
  try {
    const currentUIStorageKey = `labs-playbook-edit-ui-state-${currentEditorId}`;
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('labs-playbook-edit-ui-state-') && key !== currentUIStorageKey) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`[Labs Persistence] Cleared ${keysToRemove.length} other UI storage entries, kept ${currentUIStorageKey}`);
  } catch (error) {
    console.error('[Labs Persistence] Failed to clear other UI storage:', error);
  }
}

/**
 * Labs-specific persistence hook with debounced saving, validation, and single-state persistence
 */
export function useLabsPersistence<T>(storageKey: string) {
  /**
   * Saves the current state to localStorage with validation and versioning
   */
  const saveState = useCallback((data: T) => {
    try {
      const persistedState: PersistedLabsState<T> = {
        data,
        timestamp: Date.now(),
        version: CURRENT_VERSION,
      };

      localStorage.setItem(storageKey, JSON.stringify(persistedState));
    } catch (error) {
      console.error(`[Labs Persistence] Failed to save state for key "${storageKey}":`, error);
    }
  }, [storageKey]);

  /**
   * Debounced version of saveState to prevent too frequent saves
   */
  const debouncedSaveState = useCallback(
    debounce(saveState, DEBOUNCE_WAIT),
    [saveState]
  );

  /**
   * Loads the persisted state from localStorage with validation and migration
   */
  const loadState = useCallback((): T | null => {
    try {
      const savedState = localStorage.getItem(storageKey);
      if (!savedState) {
        return null;
      }

      const parsedState = JSON.parse(savedState);
      if (!isValidPersistedLabsState<T>(parsedState)) {
        console.error(`[Labs Persistence] Invalid state structure for key "${storageKey}"`);
        return null;
      }

      const migratedState = migrateLabsState(parsedState);
      return migratedState.data;
    } catch (error) {
      console.error(`[Labs Persistence] Failed to load state for key "${storageKey}":`, error);
      return null;
    }
  }, [storageKey]);

  /**
   * Clears the persisted state
   */
  const clearState = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`[Labs Persistence] Failed to clear state for key "${storageKey}":`, error);
    }
  }, [storageKey]);

  /**
   * Check if there is persisted state available
   */
  const hasPersistedState = useCallback(() => {
    try {
      return localStorage.getItem(storageKey) !== null;
    } catch (error) {
      console.error(`[Labs Persistence] Failed to check persisted state for key "${storageKey}":`, error);
      return false;
    }
  }, [storageKey]);

  return {
    saveState: debouncedSaveState,
    loadState,
    clearState,
    hasPersistedState,
  };
}

export default useLabsPersistence;
